/**
 * Groups transfer lines into shipments bounded by unit and measurement caps.
 * Lines are first grouped by (fromId, toId) pair, then packed into shipments
 * greedily — splitting individual product quantities when a single line exceeds
 * the remaining capacity of the current shipment.
 *
 * sequenceNumber is globally unique across all returned shipments (1-based).
 * When no constraints are set both return a single shipment per pair.
 *
 * Oversize escape hatch: when a single unit cannot fit even an EMPTY shipment
 * (its measurementValue exceeds maxMeasurementPerShipment, or a cap is 0), it is
 * forced through one unit at a time as over-cap shipments — the unit has to move
 * regardless, and refusing would otherwise loop forever opening empty shipments.
 */
export function groupTransfersIntoShipments(lines, constraints) {
    const pairMap = new Map();
    // A line with nothing to move must not open a shipment: a lane whose every line was
    // zeroed would otherwise yield an empty shipment, shifting the sequence numbers (and
    // the names/codes keyed by them) of every real shipment behind it.
    const moving = lines.filter((line) => Math.ceil(line.quantity) > 0);
    for (const line of moving) {
        const key = `${line.fromId}|${line.toId ?? ''}`;
        if (!pairMap.has(key))
            pairMap.set(key, []);
        pairMap.get(key).push(line);
    }
    const result = [];
    let globalSeq = 0;
    const openShipment = (fromId, toId) => {
        globalSeq += 1;
        const group = {
            fromId,
            toId,
            sequenceNumber: globalSeq,
            lines: [],
            totalUnits: 0,
            totalMeasurement: 0,
        };
        result.push(group);
        return group;
    };
    for (const [, pairLines] of pairMap) {
        const { fromId, toId } = pairLines[0];
        let current = openShipment(fromId, toId);
        for (const line of pairLines) {
            let remaining = Math.ceil(line.quantity);
            while (remaining > 0) {
                const capacityByUnits = constraints.maxUnitsPerShipment !== null
                    ? constraints.maxUnitsPerShipment - current.totalUnits
                    : remaining;
                let capacityByMeasurement = remaining;
                if (constraints.maxMeasurementPerShipment !== null &&
                    line.measurementValue != null &&
                    line.measurementValue > 0) {
                    const measurementLeft = constraints.maxMeasurementPerShipment - current.totalMeasurement;
                    capacityByMeasurement = Math.floor(measurementLeft / line.measurementValue);
                }
                let canAdd = Math.min(remaining, capacityByUnits, capacityByMeasurement);
                if (canAdd <= 0 && current.totalUnits > 0) {
                    // Current shipment has content — roll over to a fresh one and measure again.
                    current = openShipment(fromId, toId);
                }
                else {
                    if (canAdd <= 0) {
                        // Even an empty shipment cannot fit one unit — a fresh shipment
                        // would have identical capacity, so rolling over again would loop
                        // forever. Force one unit through as an oversize shipment.
                        canAdd = 1;
                    }
                    const existing = current.lines.find((l) => l.transferId === line.transferId);
                    if (existing) {
                        existing.quantity += canAdd;
                    }
                    else {
                        current.lines.push({
                            transferId: line.transferId,
                            productId: line.productId,
                            productName: line.productName,
                            quantity: canAdd,
                        });
                    }
                    current.totalUnits += canAdd;
                    current.totalMeasurement += canAdd * (line.measurementValue ?? 0);
                    remaining -= canAdd;
                }
            }
        }
    }
    return result;
}
//# sourceMappingURL=shipmentGrouping.js.map